import { useState } from 'react';
import { FaFacebookF, FaInstagram, FaTelegram, FaXTwitter } from 'react-icons/fa6';
import { FaAndroid, FaApple, FaChevronDown, FaEnvelope, FaHeadphones, FaTelegramPlane } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { brand, useBrandFeatures, useBrandLogo, useHasVisibleCasino } from '@/brands';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useGetContactSupportQuery } from '@/store/api/userApi';
import { useGetApkConfigQuery } from '@/store/api/systemApi';
import { setAgeModalOpen, setRulesModalOpen } from '@/store/slices/uiSlice';
import { isNativeApp } from '@/utils/platform';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useSupportLink } from '@/hooks/useSupportLink';

type FooterLink = {
  label: string;
  /** A route to open, or an action to run. Exactly one of the two. */
  to?: string;
  onClick?: () => void;
  href?: string;
};

const Footer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { supportInfo } = useAppSelector((state) => state.user);
  useGetContactSupportQuery();

  const logo = useBrandLogo();
  const support = useSupportLink();
  const { canInstall, handleInstall } = usePWAInstall();
  const [iosTip, setIosTip] = useState(false);

  const inApp = isNativeApp();
  const { data: apk } = useGetApkConfigQuery(undefined, { skip: inApp });
  const showApk = !inApp && !!apk?.isEnabled && !!apk?.apkUrl;
  const casinoOn = useHasVisibleCasino();
  const { sports: sportsOn } = useBrandFeatures();

  // Each icon opens its own platform. Replace fb/ig/x with your brand's pages when available.
  const socials = [
    { Icon: FaFacebookF, cls: 'fb', href: 'https://www.facebook.com', label: 'Facebook' },
    { Icon: FaInstagram, cls: 'ig', href: 'https://www.instagram.com', label: 'Instagram' },
    { Icon: FaTelegram, cls: 'tg', href: supportInfo?.telegram ?? 'https://telegram.org', label: 'Telegram' },
    { Icon: FaXTwitter, cls: 'x', href: 'https://x.com', label: 'X' },
  ];

  /**
   * The link columns.
   *
   * Each one is gated on the product behind it, the same way the nav and the
   * home strips are: a footer that lists Live Casino for a brand whose casino
   * the super admin revoked is a column of links that 404. A column left with
   * no links at all is dropped rather than drawn as a bare heading.
   */
  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: 'Casino',
      links: casinoOn
        ? [
            { label: 'Casino', to: '/casino/all' },
            { label: 'Live Casino', to: '/casino/all/live' },
            { label: 'TV Games', to: '/casino/all/virtual' },
            // Only when the admin has actually uploaded an APK and switched the
            // download on — otherwise this row leads nowhere.
            ...(showApk ? [{ label: `${brand.name} App`, href: apk!.apkUrl }] : []),
          ]
        : [],
    },
    {
      title: 'Sport',
      links:
        sportsOn !== false
          ? [
              { label: 'Live', to: '/inplay' },
              { label: 'Upcoming Events', to: '/all-sports/Cricket' },
              { label: 'Betting Rules', onClick: () => dispatch(setRulesModalOpen(true)) },
            ]
          : [],
    },
    {
      title: 'General',
      links: [
        { label: 'Promotions', to: '/bonuses' },
        { label: 'Gaming Policy', to: '/terms-and-conditions' },
        { label: 'Support', to: '/contact' },
      ],
    },
    {
      title: 'Account',
      links: [
        { label: 'Deposit', to: '/user/deposit' },
        { label: 'Profile', to: '/user/profile' },
        { label: 'Bets History', to: '/user/bet-history' },
      ],
    },
  ].filter((c) => c.links.length > 0);

  return (
    <footer className="site-footer">
      <style>{CSS}</style>

      {/* ── Link columns ── */}
      {columns.length > 0 && (
        <nav className="ftr-cols">
          {columns.map((col) => (
            <div key={col.title} className="ftr-col">
              <h3 className="ftr-col-title">{col.title}</h3>
              <ul className="ftr-col-list">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.href ? (
                      <a href={l.href} download target="_blank" rel="noopener noreferrer">
                        {l.label}
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={l.onClick ?? (l.to ? () => navigate(l.to!) : undefined)}
                      >
                        {l.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      )}

      {/* ── Support + socials ── */}
      <div className="ftr-support">
        <h3 className="ftr-title">24X7 Support</h3>
        <div className="ftr-socials">
          {socials.map(({ Icon, cls, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className={`ftr-soc ${cls}`}
            >
              <Icon />
            </a>
          ))}
        </div>
      </div>

      {/* ── White panel: identity, language, support, legal, apps ── */}
      <div className="ftr-panel">
        <img className="ftr-logo" src={logo} alt={brand.name} />

        {/*
          One option, because the app ships one language: there is no i18n
          library in this build, so a longer list would be a menu that changes
          nothing. It is drawn anyway because the reference gives the choice a
          fixed place — wire it up the day translations land.
        */}
        <div className="ftr-lang">
          <span className="ftr-lang-flag" aria-hidden="true">🇬🇧</span>
          <span className="ftr-lang-name">English (EN)</span>
          <FaChevronDown size={12} className="ftr-lang-caret" />
        </div>

        <div className="ftr-support-row">
          <FaHeadphones size={18} className="ftr-support-ico" />
          <div className="ftr-support-text">
            <p>Support</p>
            <p>Report problem</p>
          </div>
          <a className="ftr-sq" href={`mailto:${brand.supportEmail}`} aria-label="Email support">
            <FaEnvelope size={15} />
          </a>
          {supportInfo?.telegram && (
            <a
              className="ftr-sq ftr-sq--tg"
              href={supportInfo.telegram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Telegram support"
            >
              <FaTelegramPlane size={15} />
            </a>
          )}
          <button
            className="ftr-chat"
            onClick={() =>
              support
                ? window.open(support, '_blank', 'noopener,noreferrer')
                : navigate('/contact')
            }
          >
            Chat
          </button>
        </div>

        {/* Who runs the site and under which licence. Absent until the operator
            supplies it — see `legalText` on the brand config. */}
        {brand.legalText && <p className="ftr-legal">{brand.legalText}</p>}

        {(showApk || !inApp) && (
          <>
            <p className="ftr-apps-title">Download the app right now!</p>
            <div className="ftr-apps">
              {showApk && (
                <a
                  className="ftr-app ftr-app--android"
                  href={apk!.apkUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaAndroid size={26} />
                  <span>
                    <i>Download for</i>
                    <b>Android</b>
                  </span>
                </a>
              )}
              {!inApp && (
                <button
                  className="ftr-app ftr-app--ios"
                  onClick={() => (canInstall ? handleInstall() : setIosTip((v) => !v))}
                >
                  <FaApple size={26} />
                  <span>
                    <i>Download for</i>
                    <b>iOS</b>
                  </span>
                </button>
              )}
            </div>
            {iosTip && (
              <p className="ftr-ios-tip">
                On iPhone: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Safety marks ── */}
      <div className="ftr-bottom">
        <div className="ftr-safe">
          <img src="/images/secure.png" alt="SSL Secure" className="ftr-secure" />
          <div className="ftr-safe-text">
            <p className="ftr-safe-title">100% SAFE</p>
            <p className="ftr-safe-desc">Protected connection and encrypted data.</p>
          </div>
        </div>

        <div className="ftr-badges">
          <img
            onClick={() => dispatch(setAgeModalOpen(true))}
            src="/images/18plus.png"
            alt="18+"
            className="ftr-badge age"
          />
          <img
            onClick={() => navigate('/blank')}
            src="/images/gamecare.png"
            alt="GameCare"
            className="ftr-badge age"
          />
          <img
            onClick={() => navigate('/blank')}
            src="/images/gt.png"
            alt="GT"
            className="ftr-badge age"
          />
        </div>

        <p className="ftr-copy">© Copyright 2026. All Rights Reserved. Powered by {brand.name}.</p>
      </div>
    </footer>
  );
};

const CSS = `
  /*
   * One yellow block, top to bottom.
   *
   * The safety marks used to sit on their own white panel, which read as a
   * second footer under the first. On the reference the whole thing is the
   * brand colour and the sections are separated by rules instead.
   */
  .site-footer {
    width: 100%;
    background: var(--cb-yellow, #ffb700);
    font-family: 'Roboto', 'Roboto Condensed', sans-serif;
  }

  /* ── Link columns: two across on a phone, four on a wide screen ── */
  .ftr-cols {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px 16px;
    padding: 24px 20px 20px;
  }
  .ftr-col-title {
    margin: 0 0 14px;
    font-size: 16px;
    font-weight: 800;
    color: var(--cb-ink-strong, #111110);
  }
  .ftr-col-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 13px;
  }
  .ftr-col-list a,
  .ftr-col-list button {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    color: rgba(17,17,16,.78);
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: color .15s;
  }
  .ftr-col-list a:hover,
  .ftr-col-list button:hover { color: var(--cb-ink-strong, #111110); }

  /* ── Support ── */
  .ftr-support {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 18px 12px;
    border-top: 1px solid rgba(17,17,16,.12);
  }
  .ftr-title {
    margin: 0;
    color: var(--cb-ink-strong, #111110);
    font-size: 19px;
    font-weight: 800;
    letter-spacing: .3px;
    text-align: center;
  }
  .ftr-socials { display: flex; align-items: center; justify-content: center; gap: 14px; }
  .ftr-soc {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px;
    text-decoration: none;
    transition: transform .15s ease;
  }
  .ftr-soc:hover { transform: translateY(-2px); }
  .ftr-soc.fb { color: #1877F2; }
  .ftr-soc.ig { color: #E1306C; }
  .ftr-soc.tg { color: #229ED9; }
  .ftr-soc.x  { color: #000000; }

  /* ── White panel ── */
  .ftr-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 22px 16px;
    background: #fff;
  }
  .ftr-logo {
    height: 30px;
    width: auto;
    object-fit: contain;
    padding: 4px 10px;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-ink-strong, #111110);
  }

  .ftr-lang {
    display: flex; align-items: center; gap: 10px;
    width: 100%; max-width: 420px;
    box-sizing: border-box;
    padding: 12px 14px;
    background: var(--cb-surface, #f5f5f5);
    border-radius: var(--cb-radius-s, .5rem);
  }
  .ftr-lang-flag  { font-size: 16px; }
  .ftr-lang-name  { flex: 1; font-size: 14px; font-weight: 600; color: var(--cb-ink, #343131); }
  .ftr-lang-caret { color: rgba(52,49,49,.5); }

  .ftr-support-row {
    display: flex; align-items: center; gap: 8px;
    width: 100%; max-width: 420px;
    box-sizing: border-box;
    padding: 8px 10px;
    background: var(--cb-surface, #f5f5f5);
    border-radius: var(--cb-radius-s, .5rem);
  }
  .ftr-support-ico  { color: var(--cb-ink, #343131); flex-shrink: 0; }
  .ftr-support-text { flex: 1; min-width: 0; }
  .ftr-support-text p {
    margin: 0;
    font-size: 11.5px;
    line-height: 1.35;
    color: rgba(52,49,49,.75);
  }
  .ftr-support-text p:first-child { font-weight: 700; color: var(--cb-ink, #343131); }

  .ftr-sq {
    display: flex; align-items: center; justify-content: center;
    width: 34px; height: 34px;
    flex-shrink: 0;
    border-radius: var(--cb-radius-s, .5rem);
    background: #e4e6ea;
    color: var(--cb-ink, #343131);
    text-decoration: none;
  }
  .ftr-sq--tg { background: #229ED9; color: #fff; }
  .ftr-sq:hover { filter: brightness(.96); }

  .ftr-chat {
    flex-shrink: 0;
    padding: 9px 18px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    background: var(--cb-yellow, #ffb700);
    color: var(--cb-ink, #343131);
    font-size: 12.5px; font-weight: 800;
    text-transform: uppercase;
    cursor: pointer;
  }
  .ftr-chat:hover { filter: brightness(1.06); }

  .ftr-legal {
    margin: 0;
    max-width: 640px;
    font-size: 11px;
    line-height: 1.6;
    color: rgba(52,49,49,.6);
    text-align: left;
  }

  .ftr-apps-title {
    margin: 4px 0 0;
    font-size: 12px; font-weight: 800;
    letter-spacing: .04em; text-transform: uppercase;
    color: var(--cb-ink, #343131);
    text-align: center;
  }
  .ftr-apps {
    display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;
    width: 100%; max-width: 420px;
  }
  .ftr-app {
    flex: 1 1 0;
    min-width: 150px;
    display: flex; align-items: center; gap: 10px;
    padding: 10px 14px;
    border: none;
    border-radius: var(--cb-radius-s, .5rem);
    color: #fff;
    text-decoration: none;
    cursor: pointer;
    transition: filter .15s, transform .1s;
  }
  .ftr-app:hover  { filter: brightness(1.1); }
  .ftr-app:active { transform: scale(.98); }
  .ftr-app--android { background: #2f9e44; }
  .ftr-app--ios     { background: var(--cb-ink-strong, #111110); }
  .ftr-app span { display: flex; flex-direction: column; line-height: 1.2; text-align: left; }
  .ftr-app i { font-style: normal; font-size: 10.5px; opacity: .8; }
  .ftr-app b { font-size: 14.5px; font-weight: 800; }

  .ftr-ios-tip {
    margin: 0;
    max-width: 420px;
    padding: 9px 12px;
    background: var(--cb-surface, #f5f5f5);
    border-radius: var(--cb-radius-s, .5rem);
    font-size: 11.5px; line-height: 1.4;
    color: var(--cb-ink, #343131);
    text-align: center;
  }
  .ftr-ios-tip strong { font-weight: 800; }

  /* ── Safety marks ── */
  .ftr-bottom {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 18px 8px calc(20px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid rgba(17,17,16,.12);
  }
  .ftr-safe { display: flex; align-items: center; justify-content: center; gap: 12px; }
  .ftr-secure { height: 46px; width: auto; object-fit: contain; flex-shrink: 0; }
  .ftr-safe-text { line-height: 1.3; }
  .ftr-safe-title {
    margin: 0;
    font-size: 15px; font-weight: 800;
    color: var(--cb-ink-strong, #111110);
  }
  .ftr-safe-desc { margin: 0; font-size: 13px; color: rgba(17,17,16,.75); }

  .ftr-badges { display: flex; align-items: center; justify-content: center; gap: 12px; }
  .ftr-badge { width: 30px; height: 30px; object-fit: contain; }
  .ftr-badge.age { cursor: pointer; }

  .ftr-copy {
    margin: 0;
    text-align: center;
    font-size: 12px;
    color: rgba(17,17,16,.75);
  }

  @media (min-width: 768px) {
    .ftr-cols {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 24px;
      max-width: 960px;
      margin: 0 auto;
      padding: 28px 24px 24px;
    }
  }
  @media (max-width: 480px) {
    .ftr-title { font-size: 18px; }
    .ftr-safe { gap: 10px; }
    .ftr-secure { height: 42px; }
    .ftr-safe-title { font-size: 14px; }
    .ftr-safe-desc { font-size: 12px; }
    .ftr-copy { font-size: 11px; }
  }
`;

export default Footer;
